import { useEffect, useRef, useState } from 'react';
import type { Leverandor } from '../api/types';
import {
  NORGE,
  lagNaal,
  lastGoogleMaps,
  nokkelenBleAvvist,
  type Kartfeil,
  type Kartoppsett,
} from '../lib/googlekart';

/**
 * Kartet med nåler på.
 *
 * Google Maps eier sin egen DOM inne i `<div>`-en, og React får ikke lov til å
 * tegne der. Derfor gjøres alt i effekter mot et `ref`, og derfor bygges kartet
 * én gang og oppdateres etterpå. Bygde vi det på nytt ved hver rendring, ville
 * utsnittet hoppet tilbake hver gang noen skrev en bokstav i søkefeltet.
 *
 * **Nålene bygges bare når innholdet er annerledes.** Signaturen under er ikke
 * pynt: `leverandorer` er en ny tabell ved hver rendring selv når innholdet er
 * likt, og med tabellen som avhengighet ble hver eneste nål revet ned og satt
 * opp igjen flere ganger i sekundet. Et klikk som landet mellom nedrivingen og
 * oppsettet, traff ingenting. Det ble målt i nettleseren den gangen kartet var
 * Leaflet, og feilen ville vært akkurat den samme her.
 */

type Props = {
  leverandorer: Leverandor[];
  oppsett: Kartoppsett | null;
  valgtId: string | null;
  onVelg: (id: string | null) => void;
  onUtsnitt?: (utsnitt: { nord: number; sor: number; ost: number; vest: number }) => void;
  /**
   * Hvor kartet skal stå når det åpnes, i stedet for hele Norge.
   *
   * Finnes for forhåndsvisningen et lokale får av sin egen oppføring: der er
   * det én nål, og et kart som står på hele landet viser den som et støvkorn.
   */
  startpunkt?: { bredde: number; lengde: number; zoom: number };
  /**
   * Ser noen utenfra på dette kartet?
   *
   * Beskjedene om hvorfor kartet mangler, er skrevet til oss som drifter: de
   * nevner miljøvariabler og fakturering hos Google. De hører hjemme i appen
   * og i portalen. Et lokale som ser en forhåndsvisning av sin egen oppføring,
   * skal verken feilsøke oppsettet vårt eller få vite hva det heter.
   */
  publikum?: boolean;
};

export function Leverandorkart({
  leverandorer,
  oppsett,
  valgtId,
  onVelg,
  onUtsnitt,
  startpunkt,
  publikum = false,
}: Props) {
  const [feil, setFeil] = useState<Kartfeil>(null);
  const boks = useRef<HTMLDivElement>(null);
  const kart = useRef<google.maps.Map | null>(null);
  const naaler = useRef(new Map<string, google.maps.marker.AdvancedMarkerElement>());
  const [klar, setKlar] = useState(false);

  // Callbackene leses fra et ref, ikke fanget i effekten. Ellers måtte kartet
  // bygges på nytt hver gang forelderen rendrer.
  const kallback = useRef({ onVelg, onUtsnitt });
  kallback.current = { onVelg, onUtsnitt };

  // Leses av effekten under uten å stå i avhengighetene: startpunktet gjelder
  // bare i det kartet bygges, og et nytt objekt ved hver rendring skulle ikke
  // rive kartet ned og sette det opp igjen.
  const start = useRef(startpunkt);
  start.current = startpunkt;

  useEffect(() => {
    if (!oppsett || !boks.current || kart.current) return;
    if (!oppsett.nokkel) {
      setFeil('ingen-nokkel');
      return;
    }

    let avbrutt = false;

    void lastGoogleMaps(oppsett)
      .then((maps) => {
        if (avbrutt || !boks.current) return;

        const m = new maps.Map(boks.current, {
          mapId: oppsett.kartId || undefined,
          // Uten dette står kartet på verden. Norge er der leverandørene er.
          center: { lat: 64.5, lng: 17 },
          zoom: 4,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          // Ett fingertrykk skal rulle sida, ikke dra kartet. På mobil er et
          // kart som stjeler rullinga, en felle.
          gestureHandling: 'cooperative',
        });
        if (start.current) {
          m.setCenter({ lat: start.current.bredde, lng: start.current.lengde });
          m.setZoom(start.current.zoom);
        } else {
          m.fitBounds({
            north: NORGE.nord,
            south: NORGE.sor,
            east: NORGE.ost,
            west: NORGE.vest,
          });
        }

        m.addListener('idle', () => {
          const b = m.getBounds();
          if (!b) return;
          kallback.current.onUtsnitt?.({
            nord: b.getNorthEast().lat(),
            ost: b.getNorthEast().lng(),
            sor: b.getSouthWest().lat(),
            vest: b.getSouthWest().lng(),
          });
        });
        // Et klikk på selve kartflata lukker kortet, slik det gjør på finn.
        m.addListener('click', () => kallback.current.onVelg(null));

        kart.current = m;
        setKlar(true);
        if (nokkelenBleAvvist()) setFeil('avvist-nokkel');
      })
      .catch((e: Error) => {
        if (avbrutt) return;
        setFeil(e.message === 'ingen-nokkel' ? 'ingen-nokkel' : 'lastefeil');
      });

    return () => {
      avbrutt = true;
    };
  }, [oppsett]);

  /** Se toppkommentaren: signaturen, ikke tabellen, avgjør om noe er endret. */
  const signatur = leverandorer
    .map((l) => `${l.id}:${l.breddegrad},${l.lengdegrad}`)
    .join('|');

  useEffect(() => {
    const m = kart.current;
    if (!m || !klar) return;

    for (const naal of naaler.current.values()) naal.map = null;
    naaler.current.clear();

    for (const l of leverandorer) {
      if (l.breddegrad === null || l.lengdegrad === null) continue;
      const naal = new google.maps.marker.AdvancedMarkerElement({
        map: m,
        position: { lat: l.breddegrad, lng: l.lengdegrad },
        content: lagNaal(false),
        title: l.navn,
      });
      naal.addListener('click', () => kallback.current.onVelg(l.id));
      naaler.current.set(l.id, naal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatur, klar]);

  /** Den valgte nåla byttes ut for seg, uten å røre de andre. */
  useEffect(() => {
    for (const [id, naal] of naaler.current) {
      naal.content = lagNaal(id === valgtId);
    }
  }, [valgtId, signatur, klar]);

  /** Kartet flytter seg til den som er valgt, så kortet ikke dekker den. */
  useEffect(() => {
    if (!valgtId || !kart.current) return;
    const valgt = leverandorer.find((l) => l.id === valgtId);
    if (!valgt || valgt.breddegrad === null || valgt.lengdegrad === null) return;
    kart.current.panTo({ lat: valgt.breddegrad, lng: valgt.lengdegrad });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valgtId]);

  return (
    <div className="kartboks">
      <div className="leverandorkart" ref={boks} data-testid="leverandorkart" />
      {feil ? <Kartbeskjed feil={feil} manglerKartId={false} publikum={publikum} /> : null}
      {!feil && oppsett?.manglerKartId ? (
        <Kartbeskjed feil={null} manglerKartId publikum={publikum} />
      ) : null}
    </div>
  );
}

/**
 * Hvorfor kartet ikke er der.
 *
 * Et kart som ikke virker, ser likt ut uansett årsak: en grå flate. Uten en
 * beskjed må den som skal rette det, gjette – og feilene her har helt ulike
 * løsninger. Derfor står de fra hverandre, i klartekst.
 */
export function Kartbeskjed({
  feil,
  manglerKartId,
  publikum = false,
}: {
  feil: Kartfeil;
  manglerKartId: boolean;
  publikum?: boolean;
}) {
  /*
    Utenfor appen sier vi bare at kartet ikke kom opp.

    Alle beskjedene under er feilsøking: de nevner miljøvariabler og
    faktureringen vår hos Google. De er riktige der de hører hjemme, og feil
    å vise et lokale som bare ville se hvordan oppføringen sin tar seg ut.
    At kartet ikke lot seg tegne, er dessuten ikke noe galt med oppføringen
    deres, og det er det eneste de trenger å vite.
  */
  if (publikum) {
    return (
      <p className="kartfeil" role="status">
        Kartet lar seg ikke vise her akkurat nå. Det er ingenting galt med oppføringen deres.
      </p>
    );
  }
  if (manglerKartId) {
    return (
      <p className="kartfeil" role="status">
        Kartet mangler en kart-ID, så nålene tegnes ikke. Sett
        <span className="kode"> GOOGLE_MAPS_KART_ID</span>.
      </p>
    );
  }
  if (feil === 'ingen-nokkel') {
    return (
      <p className="kartfeil" role="status">
        Kartet er ikke satt opp ennå. Bruk listevisningen, eller sett
        <span className="kode"> GOOGLE_MAPS_NOKKEL</span>.
      </p>
    );
  }
  if (feil === 'avvist-nokkel') {
    return (
      <p className="kartfeil" role="status">
        Google avviste nøkkelen. Sjekk at den er gyldig, at fakturering er slått på, og at
        vardag.no står i henvisningsbegrensningene.
      </p>
    );
  }
  return (
    <p className="kartfeil" role="status">
      Kartet lot seg ikke laste. Nålene og kortene finnes i listevisningen.
    </p>
  );
}
